import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  Banknote,
  BadgeCheck,
  CalendarClock,
  CheckCircle2,
  Copy,
  FileCheck2,
  FileText,
  HardHat,
  Info,
  KeyRound,
  Mail,
  MapPin,
  MessageSquare,
  Pause,
  Phone,
  Plane,
  Plus,
  ShieldAlert,
  Sparkles,
  Star,
  Trash2,
  TrendingUp,
  Truck,
  UserPlus,
  Users,
  Wrench,
} from 'lucide-react';
import { Helper, JobStatusBadge, LumperStatusBadge, PageHead } from '../components/bits.jsx';
import { Sparkline } from '../components/charts.jsx';
import { Avatar, Badge, Button, Callout, Card, CardBody, CardHead, ConfirmDialog, cx, EmptyState, Field, Input, KeyValue, Loading, Modal, PhoneInput, Progress, ScoreRing, Select, Stars, Stat, Tabs, Textarea, useToast } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { compactMoney, dateLabel, hours, money, num, phone, phoneDial, relative, titleCase } from '../lib/format.js';
import { countOf } from '../lib/plain.js';

export default function LumperDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, refresh } = useApi(`/lumpers/${id}/detail`, { deps: [id] });
  const [tab, setTab] = useState('overview');
  const [note, setNote] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading && !data) {
    return (
      <>
        <PageHead title="Lumper" subtitle="Loading profile…" />
        <Loading rows={7} />
      </>
    );
  }
  if (!data?.lumper) {
    return (
      <Card>
        <EmptyState icon={HardHat} title="Lumper not found">
          <Link to="/app/lumpers">Back to the roster</Link>
        </EmptyState>
      </Card>
    );
  }

  const {
    lumper, documents, notes, jobs, payouts, offers, devices, disputes, stats, trend,
    crew = [], memberOf = [], crewSchedule = null, crewScheduleText = null, w9 = null,
  } = data;
  const name = `${lumper.firstName} ${lumper.lastName}`;

  const setStatus = async (status, noteBody) => {
    setBusy(true);
    try {
      await api.post(`/lumpers/${id}/status`, { status, note: noteBody });
      toast.ok(`${name} is now ${status}`);
      setConfirmStatus(null);
      refresh();
    } catch (err) {
      toast.err('Could not change status', err.message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/lumpers/${id}/notes`, { body: note.trim() });
      setNote('');
      refresh();
    } catch (err) {
      toast.err('Could not save the note', err.message);
    }
  };

  const setDocStatus = async (docId, status) => {
    try {
      await api.patch(`/lumpers/documents/${docId}`, { status });
      toast.ok(`Document marked ${status}`);
      refresh();
    } catch (err) {
      toast.err('Could not update the document', err.message);
    }
  };

  const missing = documents.filter((d) => d.status !== 'approved' && d.status !== 'not_required');
  const ratings = data.ratings || [];
  const spread = data.ratingSpread || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const ratingsTotal = Object.values(spread).reduce((sum, n) => sum + Number(n || 0), 0);

  return (
    <>
      <PageHead
        breadcrumb={[{ label: 'Our lumpers', to: '/app/lumpers' }, { label: name }]}
        title={name}
        subtitle={
          <span className="row gap-10 wrap">
            <LumperStatusBadge status={lumper.status} />
            <Badge tone={lumper.tier === 'crew_lead' ? 'amber' : lumper.tier === 'pro' ? 'teal' : 'muted'}>
              {lumper.tier === 'crew_lead' ? 'Crew lead' : titleCase(lumper.tier)}
            </Badge>
            <span className="dim mono">{lumper.code}</span>
            <span className="dim">{lumper.city}, {lumper.state} {lumper.zip}</span>
            {lumper.deviceCount ? <Badge tone="green" size="sm" dot>App installed</Badge> : <Badge tone="muted" size="sm">No app yet</Badge>}
          </span>
        }
        actions={
          <>
            <a href={`tel:${phoneDial(lumper.phone)}`}><Button variant="outline" size="sm" icon={Phone}>Call</Button></a>
            <a href={`mailto:${lumper.email}`}><Button variant="outline" size="sm" icon={Mail}>Email</Button></a>
            <Button variant="outline" size="sm" icon={Banknote} onClick={() => setPayOpen(true)}>Pay terms</Button>
            <Button variant="outline" size="sm" icon={KeyRound} onClick={() => setPasswordOpen(true)}>Password</Button>
            {['applied', 'screening'].includes(lumper.status) ? (
              <Button variant="primary" size="sm" icon={BadgeCheck} loading={busy} onClick={() => setStatus('active', 'Approved from the roster')}>
                Take them on
              </Button>
            ) : null}
            {lumper.status === 'active' ? (
              <Button variant="outline" size="sm" icon={Pause} onClick={() => setConfirmStatus({ status: 'paused', title: 'Pause this lumper?', body: 'They stop receiving work offers until you reactivate them.' })}>
                Pause
              </Button>
            ) : null}
            {['paused', 'suspended'].includes(lumper.status) ? (
              <Button variant="primary" size="sm" icon={CheckCircle2} onClick={() => setStatus('active', 'Reactivated')}>
                Reactivate
              </Button>
            ) : null}
            {lumper.status !== 'suspended' ? (
              <Button variant="danger" size="sm" icon={ShieldAlert} onClick={() => setConfirmStatus({ status: 'suspended', title: 'Suspend this lumper?', body: 'They are removed from the active roster and cannot claim work.' })}>
                Suspend
              </Button>
            ) : null}
          </>
        }
      />

      {missing.length ? (
        <Callout
          tone={lumper.status === 'active' ? 'red' : 'amber'}
          icon={ShieldAlert}
          title={`${missing.length} document${missing.length > 1 ? 's' : ''} need attention`}
          className="mb-20"
          actions={
            <>
              <Button size="sm" variant="primary" onClick={() => setTab('documents')}>Review documents</Button>
              <Button size="sm" variant="outline" onClick={() => setDocOpen(true)}>Record a document</Button>
            </>
          }
        >
          {missing.map((doc) => doc.label).join(' · ')}
        </Callout>
      ) : null}

      <div className="grid grid-4 mb-20">
        <Stat
          label="Jobs done"
          icon={Truck}
          value={num(stats?.completed || 0)}
          tone="amber"
          foot={`${num(lumper.jobsCompleted)} lifetime${stats?.cancelled ? ` · ${stats.cancelled} cancelled` : ''}`}
        />
        <Stat
          label="Paid so far"
          icon={Banknote}
          value={compactMoney(stats?.grossPaid)}
          tone="green"
          foot={`${compactMoney(stats?.gross30)} in the last 30 days`}
          spark={<Sparkline values={(trend || []).map((t) => t.amount)} tone="green" width={80} height={26} />}
        />
        <Stat
          label="Usual time on a job"
          icon={Activity}
          value={Number(stats?.avgHours || 0).toFixed(1)}
          unit="hrs"
          tone="blue"
          foot={`${hours(stats?.hours)} total`}
        />
        <Stat
          label="Turns up on time"
          icon={BadgeCheck}
          value={Math.round(Number(lumper.onTimeRate || 0))}
          unit="%"
          tone={Number(lumper.onTimeRate) >= 95 ? 'lime' : Number(lumper.onTimeRate) >= 88 ? 'amber' : 'red'}
          foot={`accepts ${Math.round(Number(lumper.acceptanceRate || 0))}% of offers`}
        />
      </div>

      <Tabs
        className="mb-20"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'overview', label: 'The basics', icon: Info },
          { value: 'jobs', label: 'Jobs done', icon: Truck, count: jobs.length },
          { value: 'pay', label: 'What they were paid', icon: Banknote, count: payouts.length },
          { value: 'documents', label: 'Paperwork', icon: FileCheck2, count: documents.length },
          { value: 'offers', label: 'Jobs we offered', icon: Plane, count: offers.length },
          { value: 'notes', label: 'Notes', icon: MessageSquare, count: notes.length },
        ]}
      />

      {tab === 'overview' ? (
        <div className="split-wide">
          <div className="col gap-20">
            <Card>
              <CardHead title="Who they are" icon={HardHat} />
              <CardBody>
                <div className="row gap-16">
                  {lumper.photo ? (
                    <img
                      src={lumper.photo}
                      alt={name}
                      style={{ width: 68, height: 68, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--line-strong)', flex: '0 0 68px' }}
                    />
                  ) : (
                    <Avatar name={name} tone={lumper.photoColor} size="xl" />
                  )}
                  <div className="grow">
                    <div className="row gap-10 wrap">
                      <Stars value={lumper.rating} />
                      <Badge tone="muted">{lumper.kind === 'individual' ? 'Individual' : lumper.crewName || titleCase(lumper.kind)}</Badge>
                      {lumper.experienceYears ? <Badge tone="muted">{lumper.experienceYears} yrs experience</Badge> : null}
                    </div>
                    {lumper.bio ? <p className="muted mt-12">{lumper.bio}</p> : null}
                    <div className="row gap-8 mt-12 wrap">
                      <label className="btn btn-outline btn-sm" style={{ cursor: 'pointer' }}>
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={async (event) => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = async () => {
                              try {
                                await api.post(`/lumpers/${id}/photo`, { photoDataUrl: reader.result });
                                toast.ok('Photo saved');
                                refresh();
                              } catch (err) {
                                toast.err('Could not save that photo', err.message);
                              }
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                        {lumper.photo ? 'Change photo' : 'Add a photo'}
                      </label>
                      {!lumper.photo ? (
                        <span className="dim" style={{ fontSize: 'var(--fs-xs)', alignSelf: 'center' }}>
                          The warehouse likes to know who is turning up.
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <ScoreRing value={lumper.completeness} tone={lumper.completeness >= 90 ? 'green' : 'amber'} label={`${Math.round(lumper.completeness)}%`} />
                </div>

                <div className="divider" />

                <div className="kv-grid">
                  <div className="col gap-2">
                    <span className="label">Contact</span>
                    <span className="mono">{lumper.phone || '—'}</span>
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{lumper.email}</span>
                  </div>
                  <div className="col gap-2">
                    <span className="label">Coverage</span>
                    <span>{lumper.city}, {lumper.state} {lumper.zip}</span>
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{lumper.radiusMiles} mile radius</span>
                  </div>
                  <div className="col gap-2">
                    <span className="label">Availability</span>
                    <span className="row gap-6">
                      <Badge tone={lumper.nightsReady ? 'green' : 'muted'} size="sm">Nights</Badge>
                      <Badge tone={lumper.weekendReady ? 'green' : 'muted'} size="sm">Weekends</Badge>
                    </span>
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>last active {relative(lumper.lastActiveAt)}</span>
                  </div>
                  <div className="col gap-2">
                    <span className="label">Payout</span>
                    <span>{titleCase(lumper.payoutMethod)}</span>
                    <span className="dim mono" style={{ fontSize: 'var(--fs-xs)' }}>{lumper.payoutDetail || '—'}</span>
                  </div>
                  <div className="col gap-2">
                    <span className="label">Pay terms</span>
                    <span className="num">{money(lumper.payRate, { decimals: 2 })} {lumper.payType?.replace('_', ' ')}</span>
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>applied {dateLabel(lumper.appliedAt)}</span>
                  </div>
                  <div className="col gap-2">
                    <span className="label">Background</span>
                    <Badge tone={lumper.backgroundCheck === 'cleared' ? 'green' : 'amber'} size="sm">{titleCase(lumper.backgroundCheck)}</Badge>
                    <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {lumper.referredBy ? `Referred by ${lumper.referredBy}` : 'No referral recorded'}
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHead title="What they can do and bring" icon={Wrench} />
              <CardBody>
                <div className="label mb-8">Skills</div>
                <div className="wrap-gap mb-16">
                  {(lumper.skills || []).map((skill) => <Badge key={skill} tone="amber">{skill}</Badge>)}
                  {!lumper.skills?.length ? <span className="dim">No skills recorded.</span> : null}
                </div>
                <div className="label mb-8">Equipment</div>
                <div className="wrap-gap">
                  {(lumper.equipment || []).map((item) => <Badge key={item} tone="blue">{item}</Badge>)}
                  {!lumper.equipment?.length ? <span className="dim">No equipment recorded.</span> : null}
                </div>
                <div className="divider" />
                <div className="label mb-8">Service areas</div>
                <div className="wrap-gap">
                  {(lumper.serviceAreas || []).map((area) => <Badge key={area} tone="teal">{area}</Badge>)}
                  {!lumper.serviceAreas?.length ? <span className="dim">No service areas set.</span> : null}
                </div>
              </CardBody>
            </Card>

            <CrewCard
              lumper={lumper}
              crew={crew}
              memberOf={memberOf}
              schedule={crewSchedule}
              scheduleText={crewScheduleText}
              onChanged={refresh}
            />

            <Card>
              <CardHead title="Jobs they did lately" icon={Truck} actions={<Link to="/app/jobs"><Button size="sm" variant="ghost">All work orders</Button></Link>} />
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th className="narrow">When</th>
                      <th className="narrow">Status</th>
                      <th className="right">Pallets</th>
                      <th className="right">Hours</th>
                      <th className="right">Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.slice(0, 8).map((job) => (
                      <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                        <td>
                          <div className="col gap-2">
                            <span className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{job.code}</span>
                            <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{job.siteName} · {job.customerName}</span>
                          </div>
                        </td>
                        <td className="narrow num">{dateLabel(job.windowStart)}</td>
                        <td className="narrow"><JobStatusBadge status={job.status} size="sm" dot={false} /></td>
                        <td className="right num">{job.palletsActual || job.palletsExpected}</td>
                        <td className="right num">{job.billableHours ? Number(job.billableHours).toFixed(1) : '—'}</td>
                        <td className="right num">{money(job.lumperPay)}</td>
                      </tr>
                    ))}
                    {!jobs.length ? (
                      <tr><td colSpan={6}><EmptyState icon={Truck} title="No jobs yet">Assign them from the dispatch board to get started.</EmptyState></td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="col gap-20">
            <Card>
              <CardHead title="How they are doing" icon={TrendingUp} />
              <CardBody>
                <div className="col gap-16">
                  <ReliabilityBar label="Turns up on time" value={Number(lumper.onTimeRate || 0)} />
                  <ReliabilityBar label="Says yes to jobs" value={Number(lumper.acceptanceRate || 0)} />
                  <ReliabilityBar
                    label="How good the work is"
                    value={(Number(stats?.avgJobRating || lumper.rating || 0) / 5) * 100}
                    display={`${Number(stats?.avgJobRating || lumper.rating || 0).toFixed(1)} of 5`}
                  />
                  <KeyValue label="Jobs waiting on an answer">{num(stats?.openOffers || 0)}</KeyValue>
                  <KeyValue label="Times they said no">{num(stats?.declines || 0)}</KeyValue>
                  <KeyValue label="Times they said yes">{num(stats?.accepts || 0)}</KeyValue>
                  <KeyValue label="Jobs cancelled">{num(stats?.cancelled || 0)}</KeyValue>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHead
                title="What customers said"
                subtitle={
                  ratingsTotal
                    ? `${Number(lumper.rating || 0).toFixed(1)} out of 5 from ${countOf(ratingsTotal, 'job')}`
                    : 'No ratings yet'
                }
                icon={Star}
              />
              <CardBody className="col gap-16">
                {ratingsTotal ? (
                  <>
                    <div className="row gap-20" style={{ alignItems: 'center' }}>
                      <div className="col gap-2" style={{ alignItems: 'center', minWidth: 96 }}>
                        <span className="num" style={{ fontSize: 40, fontWeight: 700, letterSpacing: '-0.03em' }}>
                          {Number(lumper.rating || 0).toFixed(1)}
                        </span>
                        <Stars value={lumper.rating} />
                        <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{countOf(ratingsTotal, 'rating')}</span>
                      </div>
                      <div className="grow rating-bars">
                        {[5, 4, 3, 2, 1].map((star) => (
                          <div key={star} className="rating-bar-row">
                            <span className="row gap-4">{star} <Star size={11} style={{ fill: 'currentColor' }} /></span>
                            <span className="progress thin"><span style={{ width: `${ratingsTotal ? (Number(spread[star] || 0) / ratingsTotal) * 100 : 0}%` }} /></span>
                            <span className="num" style={{ textAlign: 'right' }}>{spread[star] || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <Helper>Only the average is ever shown to anybody outside the office. The words stay here.</Helper>
                    <div className="col gap-10">
                      {ratings.slice(0, 6).map((rating) => (
                        <div key={rating.id} className="rating-note">
                          <div className="row between gap-10">
                            <Stars value={rating.stars} size={12} showValue={false} />
                            <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                              {dateLabel(rating.at)} · {rating.customerName || 'Customer'}
                            </span>
                          </div>
                          {rating.comment ? <div className="what">“{rating.comment}”</div> : null}
                          <div className="who mt-4">
                            {rating.ratedByName || 'Customer'} on job {String(rating.jobCode || '').replace(/^JOB-/, '')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="col gap-12">
                    <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                      Nothing yet. Ratings are left by the customer, or by the office on their behalf, once a job
                      is finished — every finished job on the job page has a “rate the crew” box.
                    </span>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHead title="Their phone" icon={Info} />
              <CardBody className="col gap-12">
                {devices.length ? (
                  devices.map((device) => (
                    <div key={device.id} className="row between gap-8">
                      <span className="row gap-8">
                        <Badge tone={device.platform === 'ios' ? 'blue' : 'green'} size="sm">{device.platform}</Badge>
                        <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{device.osVersion}</span>
                      </span>
                      <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>
                        v{device.appVersion} · seen {relative(device.lastSeenAt)}
                      </span>
                    </div>
                  ))
                ) : (
                  <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
                    No devices registered. They can install the 3Lumpers app to receive work offers.
                  </span>
                )}
              </CardBody>
            </Card>

            {disputes.length ? (
              <Card>
                <CardHead title="Problems" icon={ShieldAlert} />
                <CardBody className="col gap-10">
                  {disputes.map((dispute) => (
                    <div key={dispute.id} className="tile">
                      <div className="row between gap-8">
                        <span className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{dispute.code}</span>
                        <Badge tone={dispute.status === 'resolved' ? 'green' : 'red'} size="sm">{dispute.status}</Badge>
                      </div>
                      <p className="muted mt-4" style={{ fontSize: 'var(--fs-sm)' }}>{dispute.body}</p>
                    </div>
                  ))}
                </CardBody>
              </Card>
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === 'jobs' ? (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Customer</th>
                  <th>Site</th>
                  <th className="narrow">Window</th>
                  <th className="narrow">Status</th>
                  <th className="right">Pallets</th>
                  <th className="right">Hours</th>
                  <th className="right">Pay</th>
                  <th className="narrow">Rating</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} className="clickable" onClick={() => navigate(`/app/jobs/${job.id}`)}>
                    <td className="mono" style={{ fontSize: 'var(--fs-sm)' }}>{job.code}</td>
                    <td className="truncate">{job.customerName}</td>
                    <td className="truncate">{job.siteName}</td>
                    <td className="narrow num">{dateLabel(job.windowStart)}</td>
                    <td className="narrow"><JobStatusBadge status={job.status} size="sm" dot={false} /></td>
                    <td className="right num">{job.palletsActual || job.palletsExpected}</td>
                    <td className="right num">{job.billableHours ? Number(job.billableHours).toFixed(1) : '—'}</td>
                    <td className="right num">{money(job.lumperPay)}</td>
                    <td className="narrow">{job.rating ? <Stars value={job.rating} size={11} showValue={false} /> : <span className="dimmer">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {tab === 'pay' ? (
        <div className="split">
          <Card>
            <CardHead title="Settlements" subtitle={`${payouts.length} payouts on file`} icon={Banknote} />
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th className="narrow">Status</th>
                    <th className="right">Jobs</th>
                    <th className="right">Hours</th>
                    <th className="right">Gross</th>
                    <th className="right">Fees</th>
                    <th className="right">Net</th>
                    <th className="narrow">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((payout) => (
                    <tr key={payout.id} className="clickable" onClick={() => payout.runId && navigate(`/app/payroll/${payout.runId}`)}>
                      <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{payout.runId ? `RUN-${payout.runId}` : '—'}</td>
                      <td className="narrow"><Badge tone={payout.status === 'paid' ? 'green' : 'amber'} size="sm">{payout.status}</Badge></td>
                      <td className="right num">{payout.jobsCount}</td>
                      <td className="right num">{Number(payout.hours).toFixed(1)}</td>
                      <td className="right num">{money(payout.gross)}</td>
                      <td className="right num">{money(payout.fees)}</td>
                      <td className="right num" style={{ fontWeight: 640 }}>{money(payout.net)}</td>
                      <td className="narrow dim">{payout.paidAt ? dateLabel(payout.paidAt) : 'pending'}</td>
                    </tr>
                  ))}
                  {!payouts.length ? (
                    <tr><td colSpan={8}><EmptyState icon={Banknote} title="No settlements yet" /></td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHead title="Earnings trend" subtitle="Last 12 weeks" icon={TrendingUp} />
            <CardBody>
              <Sparkline values={(trend || []).map((t) => t.amount)} tone="green" width={280} height={90} />
              <div className="col gap-12 mt-16">
                <KeyValue label="Gross paid">{money(stats?.grossPaid)}</KeyValue>
                <KeyValue label="Pay · 30d">{money(stats?.gross30)}</KeyValue>
                <KeyValue label="Hours worked">{hours(stats?.hours)}</KeyValue>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      {tab === 'documents' ? (
        <>
        <W9Card lumper={lumper} w9={w9} onChanged={refresh} />
        <AgreementCard
          lumper={lumper}
          agreement={data.agreement}
          currentWording={data.agreementVersion}
          onChanged={refresh}
        />
        <Card>
          <CardHead
            title="Paperwork"
            subtitle="Verify the paperwork before you keep someone on the active roster"
            icon={FileCheck2}
            actions={<Button size="sm" variant="primary" onClick={() => setDocOpen(true)}>Record a document</Button>}
          />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Paperwork</th>
                  <th className="narrow">Kind</th>
                  <th className="narrow">Status</th>
                  <th className="narrow">Runs out</th>
                  <th className="narrow">Sent in</th>
                  <th className="right">Action</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <div className="col gap-6">
                        <span>{doc.label}</span>
                        {doc.fileUrl || doc.fileUrlBack ? (
                          <div className="row gap-8 wrap">
                            {doc.fileUrl ? (
                              <a href={doc.fileUrl} target="_blank" rel="noreferrer" title="Open the front">
                                <img
                                  src={doc.fileUrl}
                                  alt={`${doc.label} front`}
                                  style={{ width: 58, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--line-strong)' }}
                                />
                              </a>
                            ) : null}
                            {doc.fileUrlBack ? (
                              <a href={doc.fileUrlBack} target="_blank" rel="noreferrer" title="Open the back">
                                <img
                                  src={doc.fileUrlBack}
                                  alt={`${doc.label} back`}
                                  style={{ width: 58, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--line-strong)' }}
                                />
                              </a>
                            ) : null}
                            <span className="dimmer" style={{ fontSize: 10 }}>
                              {doc.fileUrl && doc.fileUrlBack ? 'front · back' : doc.fileUrl ? 'front only' : 'back only'}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="narrow mono dim">{doc.kind}</td>
                    <td className="narrow">
                      <Badge
                        size="sm"
                        tone={doc.status === 'approved' ? 'green' : doc.status === 'pending' ? 'amber' : doc.status === 'not_required' ? 'muted' : 'red'}
                      >
                        {titleCase(doc.status)}
                      </Badge>
                    </td>
                    <td className="narrow num">{doc.expiresAt ? dateLabel(doc.expiresAt, { year: true }) : '—'}</td>
                    <td className="narrow dim">{relative(doc.uploadedAt)}</td>
                    <td className="right">
                      <div className="row gap-6 end">
                        <Button size="sm" variant="outline" onClick={() => setDocStatus(doc.id, 'approved')} disabled={doc.status === 'approved'}>Looks right</Button>
                        <Button size="sm" variant="ghost" onClick={() => setDocStatus(doc.id, 'missing')} disabled={doc.status === 'missing'}>Not sent in</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        </>
      ) : null}

      {tab === 'offers' ? (
        <Card>
          <CardHead title="Offer history" subtitle="Every job offered to this lumper" icon={Plane} />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Customer</th>
                  <th className="narrow">Window</th>
                  <th className="right">Offer</th>
                  <th className="narrow">Distance</th>
                  <th className="narrow">They replied</th>
                  <th className="narrow">What happened</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((offer) => (
                  <tr key={offer.id} className="clickable" onClick={() => navigate(`/app/jobs/${offer.jobId}`)}>
                    <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{offer.jobCode}</td>
                    <td className="truncate">{offer.customerName}</td>
                    <td className="narrow num">{dateLabel(offer.jobWindowStart)}</td>
                    <td className="right num">{money(offer.payOffer)}</td>
                    <td className="narrow num">{offer.distanceMiles ? `${Number(offer.distanceMiles).toFixed(1)} mi` : '—'}</td>
                    <td className="narrow dim">{relative(offer.respondedAt || offer.invitedAt)}</td>
                    <td className="narrow">
                      <Badge size="sm" tone={offer.status === 'accepted' ? 'green' : offer.status === 'declined' ? 'red' : 'muted'}>
                        {offer.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {!offers.length ? <tr><td colSpan={7}><EmptyState icon={Plane} title="No offers sent yet" /></td></tr> : null}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {tab === 'notes' ? (
        <div className="split">
          <Card>
            <CardHead title="File notes" subtitle="Internal only" icon={MessageSquare} />
            <CardBody>
              <div className="row gap-8 mb-16">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note to this lumper’s file…" onKeyDown={(e) => e.key === 'Enter' && addNote()} />
                <Button variant="primary" onClick={addNote} disabled={!note.trim()}>Add note</Button>
              </div>
              <div className="col gap-12">
                {notes.map((entry) => (
                  <div key={entry.id} className="tile">
                    <div className="row between gap-8">
                      <span className="row gap-8">
                        <Badge tone={entry.kind === 'status' ? 'amber' : 'muted'} size="sm">{entry.kind}</Badge>
                        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{entry.author}</span>
                      </span>
                      <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>{relative(entry.createdAt)}</span>
                    </div>
                    <p className="muted mt-8" style={{ fontSize: 'var(--fs-sm)' }}>{entry.body}</p>
                  </div>
                ))}
                {!notes.length ? <EmptyState icon={MessageSquare} title="No notes on file" /> : null}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      <PayTermsModal open={payOpen} lumper={lumper} onClose={() => setPayOpen(false)} onSaved={() => { setPayOpen(false); refresh(); }} />
      <PasswordModal open={passwordOpen} lumper={lumper} onClose={() => setPasswordOpen(false)} />
      <DocumentModal open={docOpen} lumperId={id} onClose={() => setDocOpen(false)} onSaved={() => { setDocOpen(false); refresh(); }} />
      <ConfirmDialog
        open={Boolean(confirmStatus)}
        onClose={() => setConfirmStatus(null)}
        onConfirm={() => setStatus(confirmStatus.status)}
        title={confirmStatus?.title}
        body={confirmStatus?.body}
        confirmLabel={confirmStatus?.status === 'suspended' ? 'Suspend' : 'Pause'}
        danger={confirmStatus?.status === 'suspended'}
        loading={busy}
      />
    </>
  );
}

function ReliabilityBar({ label, value, display }) {
  const tone = value >= 95 ? 'green' : value >= 88 ? 'amber' : 'red';
  return (
    <div className="meter-row">
      <div className="grow">
        <div className="row between mb-4">
          <span style={{ fontSize: 'var(--fs-sm)' }}>{label}</span>
          <span className="num dim" style={{ fontSize: 'var(--fs-sm)' }}>{display || `${Math.round(value)}%`}</span>
        </div>
        <Progress value={value} tone={tone === 'green' ? 'lime' : tone === 'amber' ? undefined : 'red'} thin />
      </div>
    </div>
  );
}

/**
 * Set a password for somebody who cannot get in.
 *
 * There is no email in this system, so there is no "forgot my password" link —
 * a man who is locked out rings the office, and until now the office had to
 * type on the server. Set it here and read it to him.
 */
function PasswordModal({ open, lumper, onClose }) {
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const suggest = () => {
    const words = ['dock', 'pallet', 'trailer', 'forklift', 'freight', 'warehouse', 'crew', 'jack'];
    const word = words[Math.floor(Math.random() * words.length)];
    setPassword(`${word}-${Math.floor(1000 + Math.random() * 8999)}`);
  };

  const save = async () => {
    if (password.trim().length < 6) {
      toast.err('Use at least 6 characters');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/lumpers/${lumper.id}/password`, { password: password.trim() });
      setDone(true);
      toast.ok('New password set');
    } catch (err) {
      toast.err('That did not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    setPassword('');
    setDone(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Set a new password"
      subtitle={`${lumper?.firstName || ''} ${lumper?.lastName || ''}`.trim() || 'This lumper'}
      footer={
        done ? (
          <Button variant="primary" onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button variant="primary" loading={busy} onClick={save}>Set it</Button>
          </>
        )
      }
    >
      {done ? (
        <Callout tone="green" icon={CheckCircle2} title="That is the new password">
          <>
            <p className="mono" style={{ fontSize: 18 }}>{password}</p>
            <p className="muted">Read it to them on the phone. Their other devices are signed out, so the old password
            is no use to anybody.</p>
          </>
        </Callout>
      ) : (
        <>
          <Callout tone="amber" icon={KeyRound} title="They will be signed out everywhere">
            Changing a password signs that person out of every device, so the old one stops working immediately.
          </Callout>
          <Field label="New password" hint="Six characters or more. Read it to them, or text it.">
            <Input className="mono" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="dock-4821" />
          </Field>
          <div className="row gap-8">
            <Button size="sm" variant="outline" icon={Sparkles} onClick={suggest}>Make one up for me</Button>
            {password ? (
              <Button size="sm" variant="ghost" icon={Copy} onClick={() => { navigator.clipboard?.writeText(password); toast.ok('Copied'); }}>
                Copy
              </Button>
            ) : null}
          </div>
        </>
      )}
    </Modal>
  );
}

function PayTermsModal({ open, lumper, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const value = (key) => (form[key] !== undefined ? form[key] : lumper?.[key]);
  const set = (key, next) => setForm((prev) => ({ ...prev, [key]: next }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="What we pay them"
      subtitle={lumper ? `${lumper.firstName} ${lumper.lastName} · ${lumper.code}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.patch(`/lumpers/${lumper.id}/pay`, {
                  payRate: Number(value('payRate')),
                  payType: value('payType'),
                  payoutMethod: value('payoutMethod'),
                  payoutDetail: value('payoutDetail'),
                  tier: value('tier'),
                });
                toast.ok('Pay terms updated');
                onSaved();
              } catch (err) {
                toast.err('Could not update pay terms', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save pay terms
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="How much"><Input className="mono" type="number" value={value('payRate')} onChange={(e) => set('payRate', e.target.value)} /></Field>
        <Field label="Paid by">
          <Select
            value={value('payType')}
            onChange={(e) => set('payType', e.target.value)}
            options={[
              { value: 'hourly', label: 'Per hour' },
              { value: 'per_pallet', label: 'Per pallet' },
              { value: 'per_job', label: 'Per job' },
            ]}
          />
        </Field>
        <Field label="Tier">
          <Select
            value={value('tier')}
            onChange={(e) => set('tier', e.target.value)}
            options={[
              { value: 'standard', label: 'Standard' },
              { value: 'pro', label: 'Pro' },
              { value: 'crew_lead', label: 'Crew lead' },
            ]}
          />
        </Field>
        <Field label="How they get paid">
          <Select
            value={value('payoutMethod')}
            onChange={(e) => set('payoutMethod', e.target.value)}
            options={[
              { value: 'ach', label: 'ACH / direct deposit' },
              { value: 'zelle', label: 'Zelle' },
              { value: 'check', label: 'Paper check' },
              { value: 'cash', label: 'Cash on site' },
            ]}
          />
        </Field>
        <Field label="Their account or handle" className="span-2">
          <Input value={value('payoutDetail')} onChange={(e) => set('payoutDetail', e.target.value)} placeholder="Account ending 1234 / Zelle handle" />
        </Field>
      </div>
    </Modal>
  );
}

function DocumentModal({ open, lumperId, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({ kind: 'w9', label: 'W-9', status: 'approved', expiresAt: '' });
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add paperwork"
      subtitle="For paperwork that came in by email or on paper"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/lumpers/${lumperId}/documents`, {
                  ...form,
                  expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
                });
                toast.ok('Document recorded');
                onSaved();
              } catch (err) {
                toast.err('Could not record the document', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save document
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="Type">
          <Select
            value={form.kind}
            onChange={(e) => {
              const map = { w9: 'W-9', id: 'Government ID', insurance: 'Certificate of insurance', agreement: 'Contractor agreement', background: 'Background screen', direct_deposit: 'Payout method' };
              setForm((prev) => ({ ...prev, kind: e.target.value, label: map[e.target.value] || prev.label }));
            }}
            options={[
              { value: 'w9', label: 'W-9' },
              { value: 'id', label: 'Government ID' },
              { value: 'insurance', label: 'Certificate of insurance' },
              { value: 'agreement', label: 'Contractor agreement' },
              { value: 'background', label: 'Background screen' },
              { value: 'direct_deposit', label: 'Payout method' },
            ]}
          />
        </Field>
        <Field label="Label"><Input value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} /></Field>
        <Field label="Status">
          <Select
            value={form.status}
            onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
            options={[
              { value: 'approved', label: 'Looks right' },
              { value: 'pending', label: 'Still to check' },
              { value: 'missing', label: 'Not sent in' },
            ]}
          />
        </Field>
        <Field label="Expires (optional)"><Input type="date" value={form.expiresAt} onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))} /></Field>
      </div>
    </Modal>
  );
}

/**
 * The W-9 he filled out and signed in the app.
 *
 * The taxpayer number is hidden behind dots. The office can open it — somebody
 * has to type it into the 1099s — but that click is written into the history, so
 * the page never carries the whole number around by accident.
 */
const CLASSIFICATION_PLAIN = {
  individual: 'Works for himself',
  sole_proprietor: 'Sole proprietor with a business name',
  single_member_llc: 'Single-member LLC',
  llc: 'LLC',
  c_corp: 'C corporation',
  s_corp: 'S corporation',
  partnership: 'Partnership',
  trust_estate: 'Trust or estate',
  other: 'Something else',
};

/**
 * The contractor agreement he signed on his phone, or an explanation of where
 * it is. **The wording is a template** — written in plain words and modelled on
 * the standard clause set, but it has not been read by a lawyer, and this is the
 * screen that says so to the only people who can do anything about it.
 */
function AgreementCard({ lumper, agreement, currentWording, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [sendBack, setSendBack] = useState(false);
  const [note, setNote] = useState('');
  const signed = !!agreement;
  const outOfDate = signed && currentWording && agreement.version !== currentWording;
  const status = agreement?.status || (signed ? 'submitted' : null);

  const review = async (next, reason) => {
    setBusy(true);
    try {
      await api.post(`/lumpers/${lumper.id}/agreement/review`, { status: next, note: reason || null });
      toast.ok(next === 'approved' ? 'Agreement accepted' : 'Sent back to be signed again');
      setSendBack(false);
      setNote('');
      onChanged?.();
    } catch (err) {
      toast.err('Could not save that', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-20">
      <CardHead
        title="Contractor agreement"
        icon={FileCheck2}
        actions={
          <Badge tone={outOfDate ? 'amber' : status === 'approved' ? 'green' : signed ? 'amber' : 'muted'} size="sm" dot>
            {outOfDate
              ? 'Signed an older wording'
              : status === 'approved'
                ? 'Checked and on file'
                : status === 'rejected'
                  ? 'Sent back to sign again'
                  : signed
                    ? 'Signed — to check'
                    : 'Not signed yet'}
          </Badge>
        }
      />
      <CardBody className="col gap-14">
        {signed ? (
          <>
            <div>
              <KeyValue label="Signed by">{agreement.signedName}</KeyValue>
              <KeyValue label="When">{agreement.signedAt ? dateLabel(agreement.signedAt) : '—'}</KeyValue>
              <KeyValue label="Wording">{agreement.version}</KeyValue>
            </div>
            <div className="tile">
              <span className="label">His signature</span>
              <img
                src={agreement.signatureUrl}
                alt={`${agreement.signedName}'s signature`}
                style={{ maxWidth: 320, width: '100%', filter: 'invert(1) hue-rotate(180deg)' }}
              />
            </div>
            {agreement.note ? (
              <Callout tone={status === 'rejected' ? 'red' : 'blue'} title="What we told him">
                {agreement.note}
              </Callout>
            ) : null}
            <div className="row gap-8 wrap">
              {status === 'approved' ? (
                <Badge tone="green" size="sm" dot>Checked and on file</Badge>
              ) : (
                <Button size="sm" variant="primary" loading={busy} onClick={() => review('approved')}>
                  Looks right
                </Button>
              )}
              {status === 'rejected' ? null : (
                <Button size="sm" variant="ghost" onClick={() => setSendBack((open) => !open)}>
                  Send it back
                </Button>
              )}
            </div>
            {sendBack ? (
              <div className="tile col gap-10">
                <Field label="Why is it coming back?" hint="He sees this in the app, so keep it plain">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Sign inside the box, please" />
                </Field>
                <div className="row gap-8 end">
                  <Button size="sm" variant="ghost" onClick={() => setSendBack(false)}>Cancel</Button>
                  <Button size="sm" variant="danger" loading={busy} onClick={() => review('rejected', note)}>
                    Send it back to be signed again
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
            Nothing signed yet. {lumper.firstName} does this on his phone — Me → your paperwork →{' '}
            <strong>Contractor agreement</strong> — and it lands here the moment he signs it. If he signed paper
            instead, record it below.
          </p>
        )}
        <Helper>
          <strong>This wording is a template.</strong> It is written in plain words and covers the usual clauses —
          that he is a contractor and not an employee, that he sets his own tax aside, safety, and how either side
          ends it — but no lawyer has read it. Have yours look before a real crew signs.
        </Helper>
      </CardBody>
    </Card>
  );
}

function W9Card({ lumper, w9, onChanged }) {
  const toast = useToast();
  const [tin, setTin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendBack, setSendBack] = useState(false);
  const [note, setNote] = useState('');

  if (!w9) {
    return (
      <Card className="mb-20">
        <CardHead
          title="Tax form (W-9)"
          icon={FileText}
          actions={<Badge tone={lumper.w9OnFile ? 'green' : 'amber'} size="sm" dot>{lumper.w9OnFile ? 'On file' : 'Not signed yet'}</Badge>}
        />
        <CardBody>
          <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
            Nothing filled in yet. {lumper.firstName} can do it on his phone — Me → Your paperwork →{' '}
            <strong>Tax form (W-9)</strong> — and it lands here the moment he signs it. If he already handed you
            paper, record it below and mark it “Looks right”.
          </p>
        </CardBody>
      </Card>
    );
  }

  const review = async (status, reason) => {
    setBusy(true);
    try {
      await api.post(`/lumpers/${lumper.id}/w9/review`, { status, note: reason || null });
      toast.ok(status === 'approved' ? 'W-9 accepted' : 'Sent back to be redone');
      setSendBack(false);
      setNote('');
      onChanged();
    } catch (err) {
      toast.err('Could not save that', err.message);
    } finally {
      setBusy(false);
    }
  };

  const reveal = async () => {
    setBusy(true);
    try {
      const payload = await api.post(`/lumpers/${lumper.id}/w9/reveal`, { reason: 'Office opened the number' });
      setTin(payload.data.tin);
      toast.info('The whole number is showing', 'This was written into the history.');
    } catch (err) {
      toast.err('Could not unlock it', err.message);
    } finally {
      setBusy(false);
    }
  };

  const tone = w9.status === 'approved' ? 'green' : w9.status === 'submitted' ? 'amber' : 'red';
  const tinShown = tin
    ? `${tin.slice(0, 3)}-${tin.slice(3, 5)}-${tin.slice(5)}`
    : w9.tinMasked;

  return (
    <Card className="mb-20">
      <CardHead
        title="Tax form (W-9)"
        subtitle={`Signed by ${w9.signedName} on ${dateLabel(w9.signedAt, { year: true })}`}
        icon={FileText}
        actions={
          <div className="row gap-6">
            <Badge tone={tone} size="sm" dot>
              {w9.status === 'approved' ? 'Checked and accepted' : w9.status === 'submitted' ? 'Waiting on us' : 'Sent back'}
            </Badge>
            {w9.status === 'approved' ? null : (
              <Button size="sm" variant="primary" loading={busy} onClick={() => review('approved')}>Looks right</Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSendBack((open) => !open)}>Send it back</Button>
          </div>
        }
      />
      <CardBody>
        <div className="grid grid-2" style={{ gap: 18 }}>
          <div className="col gap-10">
            <KeyValue label="Name on the form">{w9.legalName}</KeyValue>
            {w9.businessName ? <KeyValue label="Business name">{w9.businessName}</KeyValue> : null}
            <KeyValue label="What he is">
              {CLASSIFICATION_PLAIN[w9.classification] || titleCase(w9.classification)}
            </KeyValue>
            <KeyValue label="Address">
              {[w9.address, `${w9.city}, ${w9.state} ${w9.zip}`].filter(Boolean).join(' · ')}
            </KeyValue>
            <KeyValue label={w9.tinType === 'ein' ? 'Business number (EIN)' : 'Social security number'}>
              <span className="row gap-8">
                <span className="mono" style={{ letterSpacing: '0.06em' }}>{tinShown}</span>
                {tin ? (
                  <Button size="sm" variant="ghost" onClick={() => setTin(null)}>Hide it again</Button>
                ) : (
                  <Button size="sm" variant="outline" loading={busy} onClick={reveal}>Show the whole number</Button>
                )}
              </span>
            </KeyValue>
            {w9.note ? <KeyValue label="Our note">{w9.note}</KeyValue> : null}
          </div>
          <div className="col gap-8">
            <span className="label">His signature</span>
            {w9.signatureUrl ? (
              <img
                src={w9.signatureUrl}
                alt={`${w9.signedName} signature`}
                style={{ background: '#fff', borderRadius: 10, border: '1px solid var(--line-strong)', padding: 6, maxWidth: 320 }}
              />
            ) : (
              <span className="dim">No signature saved.</span>
            )}
            <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
              He signed the IRS wording ({w9.certification}) on {dateLabel(w9.signedAt, { year: true })}.
            </span>
          </div>
        </div>

        {sendBack ? (
          <div className="col gap-10 mt-16">
            <Field label="What does he need to fix?">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="The number was cut off — sign again." />
            </Field>
            <div className="row gap-8">
              <Button variant="danger" loading={busy} onClick={() => review('rejected', note)}>Send it back to him</Button>
              <Button variant="ghost" onClick={() => setSendBack(false)}>Never mind</Button>
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * The crew.
 *
 * A crew is a lead plus the men he brings. The men only need a name and a phone
 * number to be useful — he texts them the link, and the ones who want their own
 * pay and their own rating finish signing up from that link.
 */
const CREW_DAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
];

function CrewCard({ lumper, crew = [], memberOf = [], schedule, scheduleText, onChanged }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(schedule?.days || [1, 2, 3, 4, 5, 6]);
  const [start, setStart] = useState(schedule?.start || '06:00');
  const [hours, setHours] = useState(schedule?.hours || 8);
  const [scheduleBusy, setScheduleBusy] = useState(false);

  const add = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.err('We need his name and a phone number', 'That is all it takes to put him on the crew.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/lumpers/${lumper.id}/crew`, form);
      toast.ok(`${form.name} added`, 'Send him the text below so he can join from his phone.');
      setForm({ name: '', phone: '' });
      onChanged();
    } catch (err) {
      toast.err('Could not add him', err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (member) => {
    try {
      await api.del(`/lumpers/${lumper.id}/crew/${member.id}`);
      toast.ok(`${member.name} taken off the crew`);
      onChanged();
    } catch (err) {
      toast.err('Could not take him off', err.message);
    }
  };

  const saveSchedule = async () => {
    setScheduleBusy(true);
    try {
      await api.patch(`/lumpers/${lumper.id}/crew/schedule`, { days, start, hours: Number(hours) });
      toast.ok('Weekly schedule saved', 'This is what the dispatch board will show for his crew.');
      onChanged();
    } catch (err) {
      toast.err('Could not save the schedule', err.message);
    } finally {
      setScheduleBusy(false);
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.ok(`${what} copied`, 'Paste it into a text message to him.');
    } catch {
      toast.err('Your browser would not let me copy that', text);
    }
  };

  const joined = crew.filter((m) => m.status === 'joined');
  const invited = crew.filter((m) => m.status !== 'joined');

  return (
    <Card>
      <CardHead
        title="His crew"
        subtitle="The men he brings with him. Name and phone number is all we need — they can sign up later if they want their own pay."
        icon={Users}
      />
      <CardBody>
        {memberOf.length ? (
          <Callout tone="blue" icon={Users} className="mb-16">
            {lumper.firstName} works on {memberOf[0].leadFirstName} {memberOf[0].leadLastName}&rsquo;s crew
            {memberOf[0].leadCrewName ? ` (${memberOf[0].leadCrewName})` : ''}.
          </Callout>
        ) : null}

        {crew.length ? (
          <div className="col gap-10 mb-16">
            {crew.map((member) => (
              <div key={member.id} className="row gap-10" style={{ alignItems: 'flex-start' }}>
                <Avatar name={member.name} tone={member.status === 'joined' ? 'teal' : 'muted'} size="sm" />
                <div className="col gap-2 grow">
                  <span className="row gap-6 wrap">
                    <span style={{ fontWeight: 600 }}>{member.name}</span>
                    {member.status === 'joined' ? (
                      <Badge tone="teal" size="sm" dot>has the app</Badge>
                    ) : (
                      <Badge tone="amber" size="sm">invited</Badge>
                    )}
                    {member.lumperId ? <Badge tone="muted" size="sm">{member.lumperCode}</Badge> : null}
                  </span>
                  <span className="row gap-10 dim" style={{ fontSize: 'var(--fs-xs)' }}>
                    <span className="row gap-4"><Phone size={11} /> {phone(member.phone)}</span>
                    <span className="row gap-4"><Users size={11} /> code {member.inviteCode}</span>
                    {member.lumperJobs ? <span>{member.lumperJobs} jobs done</span> : null}
                  </span>
                </div>
                <div className="row gap-6">
                  <Button
                    size="sm"
                    variant="outline"
                    icon={Copy}
                    onClick={() => copy(member.inviteMessage, 'Message')}
                  >
                    Text to send
                  </Button>
                  <Button size="sm" variant="ghost" icon={Trash2} onClick={() => remove(member)}>Take off</Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted mb-16" style={{ fontSize: 'var(--fs-sm)' }}>
            No crew on file yet. If {lumper.firstName} shows up with men, add them here so we know who is on the job and
            so they can be paid and rated on their own later.
          </p>
        )}

        <div className="grid grid-3" style={{ gap: 12, alignItems: 'end' }}>
          <Field label="His name">
            <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Marcus Bell" />
          </Field>
          <Field label="His phone number" hint="We never share it with customers.">
            <PhoneInput value={form.phone} onChange={(value) => setForm((p) => ({ ...p, phone: value }))} placeholder="(305) 123-4567" />
          </Field>
          <Button variant="primary" icon={UserPlus} loading={busy} onClick={add}>Add him to the crew</Button>
        </div>

        {invited.length ? (
          <p className="dim mt-12" style={{ fontSize: 'var(--fs-xs)' }}>
            {countOf(invited.length, 'man')} {invited.length === 1 ? 'has' : 'have'} not signed up yet. Use
            <strong> Text to send</strong> to paste the invite into a message. The ones who sign up stop counting as
            invited and get their own pay and rating.
          </p>
        ) : null}

        <div className="divider" />

        <div className="label mb-8">Days his crew normally works</div>
        <div className="row gap-6 wrap mb-12">
          {CREW_DAYS.map((day) => {
            const on = days.includes(day.value);
            return (
              <Button
                key={day.value}
                size="sm"
                variant={on ? 'primary' : 'outline'}
                onClick={() => setDays((prev) => (on ? prev.filter((d) => d !== day.value) : [...prev, day.value]))}
              >
                {day.label}
              </Button>
            );
          })}
        </div>
        <div className="grid grid-3" style={{ gap: 12, alignItems: 'end' }}>
          <Field label="Usual start time">
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
          <Field label="Hours a day">
            <Input className="mono" type="number" min="1" max="16" value={hours} onChange={(e) => setHours(e.target.value)} />
          </Field>
          <Button variant="outline" loading={scheduleBusy} onClick={saveSchedule}>Save the weekly schedule</Button>
        </div>
        {scheduleText ? (
          <p className="muted mt-12" style={{ fontSize: 'var(--fs-sm)' }}>On file: {scheduleText}</p>
        ) : (
          <p className="dim mt-12" style={{ fontSize: 'var(--fs-xs)' }}>Nothing saved yet.</p>
        )}
      </CardBody>
    </Card>
  );
}
